import React, { useEffect, useRef } from 'react';
import Select from 'react-select';
import {  IJudgeInfo, IProjectInfo } from '../_Core/CoreTypes';
import Button from '../_Core/Button';
import './ManualScoreEntry.css';
import { IJudgingInfoResults, IScoresheetImportLine } from './JudgingResultsTypes';

interface ManualScoreEntryProps {
  selectedRound: any;
  judgingInfo?: IJudgingInfoResults;
  selectedJudge?: IJudgeInfo;
  setSelectedJudge: (judge?: IJudgeInfo) => void;
  manualScores: IScoresheetImportLine[];
  projectSearchQuery: string;
  setProjectSearchQuery: (query: string) => void;
  filteredProjects: IProjectInfo[];
  selectedProject?: IProjectInfo;
  setSelectedProject: (project?: IProjectInfo) => void;
  currentScoreInput: string;
  setCurrentScoreInput: (score: string) => void;
  onAddScore: () => void;
  onRemoveScore: (index: number) => void;
  onSubmitScores: () => void;
  onLoadJudgingInfo: () => void;
}

const ManualScoreEntry: React.FC<ManualScoreEntryProps> = ({
  selectedRound,
  judgingInfo,
  selectedJudge,
  setSelectedJudge,
  manualScores,
  projectSearchQuery,
  setProjectSearchQuery,
  filteredProjects,
  selectedProject,
  setSelectedProject,
  currentScoreInput,
  setCurrentScoreInput,
  onAddScore,
  onRemoveScore,
  onSubmitScores,
  onLoadJudgingInfo
}) => {
  const projectSearchRef = useRef<HTMLInputElement>(null);
  const scoreInputRef = useRef<HTMLInputElement>(null);
  
  // Load judging info when tab opens
  useEffect(() => {
    if (!judgingInfo) {
      onLoadJudgingInfo();
    }
  }, []);
  
  // Focus project search when judge is selected
  useEffect(() => {
    if (selectedJudge && projectSearchRef.current) {
      projectSearchRef.current.focus();
    }
  }, [selectedJudge]);
  
  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, nextAction?: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (nextAction) {
        nextAction();
      } else {
        onAddScore();
      }
    }
  };
  
  const judgeOptions = judgingInfo?.judges?.map(judge => ({
    value: judge.id,
    label: `${judge.firstName} ${judge.lastName}`
  })) || [];
  
  return (
    <div className="form-horizontal manual-score-entry-form">
      <div className="form-group">
        <label className="col-sm-3 control-label">Round</label>
        <div className="col-sm-9">
          <input 
            type="text" 
            className="form-control" 
            value={selectedRound?.Name || ''} 
            readOnly 
          />
        </div>
      </div>
      
      <div className="form-group">
        <label className="col-sm-3 control-label">Judge *</label>
        <div className="col-sm-9">
          <Select
            options={judgeOptions}
            value={judgeOptions.find(opt => opt.value === selectedJudge?.id)}
            onChange={(option) => {
              const judge = judgingInfo?.judges?.find(j => j.id === option?.value);
              setSelectedJudge(judge);
            }}
            placeholder="Select a judge..."
            isClearable
          />
        </div>
      </div>
      
      {selectedJudge && (
        <>
          <div className="form-group">
            <label className="col-sm-3 control-label">Entry Search *</label>
            <div className="col-sm-9">
              <Select
                 options={filteredProjects.map(project => ({
                   value: project.id,
                     label: `${project.projectId} - ${project.title}`   
                 }))}
                onChange={(option) => { 
                    const project = judgingInfo?.projects?.find(p => p.id === option?.value);
                    setSelectedProject(project);
                    if(project && scoreInputRef.current){
                        scoreInputRef.current.focus();
                    }
                }}
                placeholder="Select a project..."
                isClearable
                />
            </div>
          </div>
                    
          
          {selectedProject && (
            <div className="form-group">
              <label className="col-sm-3 control-label">Selected Project</label>
              <div className="col-sm-9">
                <div className="alert alert-info">
                  <strong>{selectedProject.projectId}</strong> - {selectedProject.title}
                </div>
              </div>
            </div>
          )}
          
          <div className="form-group">
            <label className="col-sm-3 control-label">Score *</label>
            <div className="col-sm-6">
              <input
                ref={scoreInputRef}
                type="number"
                className="form-control"
                placeholder="Enter score (0-100)"
                value={currentScoreInput}
                onChange={(e) => setCurrentScoreInput(e.target.value)}
                onKeyDown={handleKeyDown}
                min="0"
                max="100"
                step="0.01"
              />
            </div>
            <div className="col-sm-3">
              <Button 
                type="add" 
                text="Add Score" 
                onClick={onAddScore}
                disabled={!selectedJudge || !selectedProject || !currentScoreInput.trim()}
              />
            </div>
          </div>
          
          {manualScores.length > 0 && (
            <>
              <div className="form-group">
                <div className="col-sm-12">
                  <h4>Scores to Submit ({manualScores.length})</h4>
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Judge</th>
                        <th>Entry Id</th>
                        <th>Score</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manualScores.map((score, index) => {
                        const judge = judgingInfo?.judges?.find(j => j.id === +score.judgeId);
                        return (
                          <tr key={index}>
                            <td>{judge ? `${judge.firstName} ${judge.lastName}` : score.judgeId}</td>
                            <td>{score.projectDisplayId}</td>
                            <td>{score.score}</td>
                            <td>
                              <button 
                                className="btn btn-xs btn-danger"
                                onClick={() => onRemoveScore(index)}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="form-group">
                <div className="col-sm-12">
                  <Button 
                    type="save" 
                    text={`Submit ${manualScores.length} Score(s)`}
                    onClick={onSubmitScores}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ManualScoreEntry;